import { useMemo } from "react";
import { useGetCompaniesActivitiesQuery } from "api";
import { Activities, CampaignActivities } from "../types/apiTypes";
import { formattedDate } from "../utils/formatDate";
import { ActivitiesStatus, NotificationTypes } from "../types/enums";
import { useAppSelector } from "./useReduxHooks";

type TransformedData = Record<NotificationTypes, Record<string, Activities[]>>;

const groupActivitiesByDate = (
  records: CampaignActivities[],
): TransformedData => {
  if (!records)
    return {
      [NotificationTypes.scraping]: {},
      [NotificationTypes.analysis]: {},
      [NotificationTypes.account_plan_generation]: {},
      [NotificationTypes.sdr_playbook_generation]: {},
    };

  return records.reduce(
    (acc, record) => {
      const {
        type,
        account_id,
        name,
        status,
        progress,
        date,
        user,
        member_id,
        approximate_time,
      } = record;

      if (!NotificationTypes[type] || !ActivitiesStatus[status]) return acc;

      const dateKey = date.split("T")[0];

      const target = acc[type];
      if (!target[dateKey]) target[dateKey] = [];

      target[dateKey].push({
        id:
          type === NotificationTypes.sdr_playbook_generation
            ? member_id
            : account_id,
        name,
        status:
          status === ActivitiesStatus.queued ||
          status === ActivitiesStatus.updating
            ? ActivitiesStatus.in_progress
            : status,
        progress,
        date: dateKey,
        user,
        approximate_time,
      });

      return acc;
    },
    {
      [NotificationTypes.scraping]: {},
      [NotificationTypes.analysis]: {},
      [NotificationTypes.account_plan_generation]: {},
      [NotificationTypes.sdr_playbook_generation]: {},
    } as TransformedData,
  );
};

const formatGroupedActivities = <T extends { status: string }>(
  data: Record<string, T[]>,
): Array<{ title: string; items: Record<string, T[]> }> => {
  return Object.entries(data).map(([dateStr, entries]) => {
    const items: Record<string, T[]> = {};

    entries.forEach((el) => {
      if (items[el.status]) {
        items[el.status].push(el);
      } else {
        items[el.status] = [el];
      }
    });

    return {
      title: formattedDate(dateStr),
      items,
    };
  });
};

export const useFormattedActivities = (campaign_id: string) => {
  const { data } = useGetCompaniesActivitiesQuery(campaign_id);
  const { isEditor } = useAppSelector((state) => state.company.userRoles);

  const transformed = useMemo(
    () =>
      groupActivitiesByDate(
        data
          ?.filter(
            ({ status }) => isEditor || status !== ActivitiesStatus.failed,
          )
          .sort((a, b) => (a.date > b.date ? -1 : 1)) || [],
      ),
    [data, isEditor],
  );

  const scraping = useMemo(
    () => formatGroupedActivities(transformed.scraping),
    [transformed.scraping, data],
  );

  const analysis = useMemo(
    () => formatGroupedActivities(transformed.analysis),
    [transformed.analysis, data],
  );

  const accountPlan = useMemo(
    () => formatGroupedActivities(transformed.account_plan_generation),
    [transformed.account_plan_generation, data],
  );

  const playbook = useMemo(
    () => formatGroupedActivities(transformed.sdr_playbook_generation),
    [transformed.sdr_playbook_generation, data],
  );

  return useMemo(
    () => ({ scraping, analysis, accountPlan, playbook, loading: !data }),
    [scraping, analysis, accountPlan, playbook, data],
  );
};
